import Vue from 'vue';
import _ from 'lodash';
import { lsApiService, lsErrorService } from 'ls-vue-common';
import {
  SCHEDULES__MUTATE_SCHEDULE,
  RECOMMENDATIONS__MUTATE_RECOMMENDATIONS,
  RECOMMENDATIONS__MUTATE_RECOMMENDATION,
  RECOMMENDATIONS__MUTATE_LOADING_STATUS,
  RECOMMENDATIONS__GET_RECOMMENDATION,
  RECOMMENDATIONS__REJECT_QUOTE,
  RECOMMENDATIONS__APPROVE_QUOTE,
  RECOMMENDATIONS__GROUPED_BY_PROPERTY,
  RECOMMENDATIONS__RESET,
} from '@/store/types';
import { lsModuleHelpers } from '../helpers';

const createInitialState = () => {
  return {
    recommendationsById: {},
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
  };
};

export const mutations = {
  [RECOMMENDATIONS__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [RECOMMENDATIONS__MUTATE_RECOMMENDATIONS](state, payload) {
    // recommendations are returned with vanilla id field from the api
    state.recommendationsById = _.keyBy(payload, 'id');
  },

  [RECOMMENDATIONS__MUTATE_RECOMMENDATION](state, payload) {
    const { id } = payload;
    Vue.set(state.recommendationsById, id, {
      ...state.recommendationsById[id],
      ...payload,
    });
  },

  [RECOMMENDATIONS__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
};

export const actions = {
  [RECOMMENDATIONS__GET_RECOMMENDATION](
    { commit, state, rootState },
    { property_id, recommendation_id, forceRefresh = false } = {}
  ) {
    let requestPromise = Promise.resolve();
    const { customer_id } = rootState.auth.user;
    const recommendationLoaded = Boolean(_.get(state.recommendationsById, recommendation_id));

    if (forceRefresh || !recommendationLoaded) {
      commit(RECOMMENDATIONS__MUTATE_LOADING_STATUS, { isLoading: true });

      requestPromise = lsApiService.request({
        method: 'GET',
        route: `/customers/${customer_id}/properties/${property_id}/recommendations/${recommendation_id}`,
        params: {
          include: ['contractor.user', 'service', 'schedule'],
        },
      });

      return requestPromise
        .then(({ data }) => {
          commit(RECOMMENDATIONS__MUTATE_RECOMMENDATION, data);
        })
        .catch((error) => {
          lsErrorService.log({ error, showAlert: true });
        })
        .finally(() => {
          commit(RECOMMENDATIONS__MUTATE_LOADING_STATUS, {
            isLoading: false,
          });
        });
    }
    return requestPromise;
  },

  [RECOMMENDATIONS__REJECT_QUOTE](
    { commit, rootState },
    { property_id, recommendation_id, details, onSuccess = _.noop }
  ) {
    commit(RECOMMENDATIONS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });
    const { customer_id } = rootState.auth.user;
    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/recommendations/${recommendation_id}/reject`,
        payload: {
          status_details: details,
        },
      })
      .then(({ data }) => {
        commit(RECOMMENDATIONS__MUTATE_RECOMMENDATION, data);
        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(RECOMMENDATIONS__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },

  [RECOMMENDATIONS__APPROVE_QUOTE](
    { commit, rootState },
    { property_id, recommendation_id, onSuccess }
  ) {
    commit(RECOMMENDATIONS__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/recommendations/${recommendation_id}/approve`,
      })
      .then(({ data }) => {
        commit(RECOMMENDATIONS__MUTATE_RECOMMENDATION, data);

        if (data.schedule) {
          commit(SCHEDULES__MUTATE_SCHEDULE, data.schedule);
        }

        onSuccess();
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(RECOMMENDATIONS__MUTATE_LOADING_STATUS, {
          isUpdating: false,
        });
      });
  },
};

export const getters = {
  [RECOMMENDATIONS__GROUPED_BY_PROPERTY](state) {
    return _.groupBy(state.recommendationsById, 'property_id');
  },
};

export const recommendations = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
